import React, { useState, useRef, useCallback, ReactNode, useEffect } from 'react';
import { Flower, Sparkles } from 'lucide-react';

interface Props {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

export const PullToRefresh: React.FC<Props> = ({ onRefresh, children }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasCrossedThreshold, setHasCrossedThreshold] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const isPulling = useRef(false);
  const threshold = 120; // Increased from 90 - need more pull
  const maxPull = 180;

  // Check if page is scrolled to top
  const isAtTop = useCallback(() => {
    // Check window scroll
    if (window.scrollY > 5) return false;
    
    // Check if any parent is scrolled
    const container = containerRef.current;
    if (!container) return true;
    
    let el: HTMLElement | null = container;
    while (el) {
      if (el.scrollTop > 5) return false;
      el = el.parentElement;
    }
    return true;
  }, []);

  // Block browser's pull-to-refresh ONLY when our custom pull is active
  useEffect(() => {
    const preventBrowserPull = (e: TouchEvent) => {
      // Only block when we're actively pulling down with our custom component
      if (isPulling.current && pullDistance > 5) {
        e.preventDefault();
      }
    };

    // Must use passive: false to allow preventDefault
    document.addEventListener('touchmove', preventBrowserPull, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventBrowserPull);
    };
  }, [pullDistance]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    // Skip for certain elements
    if (
        target.tagName === 'CANVAS' || 
        (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'range') || 
        target.closest('.no-ptr') ||
        target.closest('[data-radix-scroll-area-viewport]')
    ) {
        return;
    }
    
    // Only start pull if at top and not already refreshing
    if (isAtTop() && !isRefreshing) {
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, [isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) {
      return;
    }

    // If user scrolled down, cancel pull and let browser handle
    if (!isAtTop()) {
      isPulling.current = false;
      setPullDistance(0);
      return;
    }
    
    const currentY = e.touches[0].clientY;
    const distance = currentY - startY.current;
    
    if (distance > 0) {
      // Apply stronger resistance for natural feel - requires more pull
      const resistedDistance = Math.min(maxPull, Math.pow(distance, 0.7) * 1.8);
      setPullDistance(resistedDistance);
      
      // Haptic feedback at threshold
      if (resistedDistance >= threshold && !hasCrossedThreshold) {
        setHasCrossedThreshold(true);
        if (navigator.vibrate) navigator.vibrate(15);
      } else if (resistedDistance < threshold && hasCrossedThreshold) {
        setHasCrossedThreshold(false);
      }
    } else {
      // User is scrolling up, cancel pull
      isPulling.current = false;
      setPullDistance(0);
    }
  }, [isRefreshing, hasCrossedThreshold, isAtTop]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current || isRefreshing) {
      isPulling.current = false;
      return;
    }
    
    isPulling.current = false;
    
    if (pullDistance >= threshold) {
      // Trigger refresh
      setIsRefreshing(true);
      setPullDistance(threshold);
      try {
        if (navigator.vibrate) navigator.vibrate([30, 50]);
        await onRefresh();
      } finally {
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
          setHasCrossedThreshold(false);
        }, 800);
      }
    } else {
      // Snap back
      setPullDistance(0);
      setHasCrossedThreshold(false);
    }
  }, [isRefreshing, pullDistance, onRefresh]);

  const rotation = isRefreshing ? 0 : pullDistance * 3;
  const scale = Math.min(1.1, pullDistance / threshold);
  const opacity = Math.min(1, pullDistance / 40);
  const haloScale = hasCrossedThreshold ? 1.4 : 0.8;

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-visible"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      {pullDistance > 0 && (
        <div 
          className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none z-50"
          style={{ 
            height: threshold + 'px', 
            top: 0, 
            transform: `translateY(${pullDistance - threshold}px)`, 
            opacity: opacity 
          }}
        >
          <div className="relative flex items-center justify-center">
            <div 
              className={'absolute rounded-full border-2 border-dashed border-saffron/40 ' + (isRefreshing ? 'animate-spin border-saffron' : '')}
              style={{ width: '64px', height: '64px', transform: `scale(${haloScale}) rotate(${rotation / 2}deg)`, opacity: hasCrossedThreshold || isRefreshing ? 1 : 0.3 }}
            />
            {hasCrossedThreshold && !isRefreshing && (
              <div className="absolute inset-0 animate-ping opacity-50">
                <Sparkles className="text-saffron w-full h-full" />
              </div>
            )}
            <div 
              className={'relative p-2.5 rounded-full shadow-2xl flex items-center justify-center ' + (isRefreshing ? 'bg-saffron text-stone-900 ring-4 ring-saffron/20' : hasCrossedThreshold ? 'bg-white dark:bg-stone-800 text-saffron scale-110 border-2 border-saffron' : 'bg-white dark:bg-stone-800 text-templeRed dark:text-stone-400 border border-gray-100 dark:border-stone-700')}
              style={{ width: '52px', height: '52px', transform: !isRefreshing ? `rotate(${rotation}deg) scale(${scale})` : '' }}
            >
              <Flower className={isRefreshing ? 'animate-pulse' : ''} size={28} />
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center gap-1">
            <p className={'text-[11px] font-black uppercase tracking-[0.2em] ' + (isRefreshing ? 'text-saffron animate-pulse' : hasCrossedThreshold ? 'text-saffron' : 'text-gray-400 dark:text-stone-500')}>
              {isRefreshing ? 'Receiving Grace...' : hasCrossedThreshold ? 'Release for Blessing' : 'Pull for Darshan'}
            </p>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div 
        className={isRefreshing ? 'blur-[0.5px]' : ''} 
        style={pullDistance > 0 ? { transform: `translateY(${pullDistance}px)` } : {}}
      >
        {children}
      </div>
    </div>
  );
};
